import { useEffect } from 'react';
import { useFile, useSelection, useVisualizer, useUI } from '../context/AppContext.jsx';
import { useIKSolver } from './useIKSolver.js';
import { featureToWorld } from '../utils/coordinateTransforms.js';

export function useUIHandlers() {
  const { currentWaypoints, currentFeatures } = useFile();
  const {
    selectedWaypointIndex,
    setSelectedWaypointIndex,
    setSelectedIsViaPoint,
    setSelectedFeatureIndex,
    clearMultiSelect,
  } = useSelection();
  const { visualizer } = useVisualizer();
  const { jumpToWaypointEnabled, showStatus } = useUI();

  const { ikSolver } = useIKSolver();

  useEffect(() => {
    const handleWaypointSelected = (e) => {
      const index = e.detail.index;
      setSelectedWaypointIndex(index);
      setSelectedIsViaPoint(false);
      clearMultiSelect();
      
      if (visualizer) {
        visualizer.selectWaypoint(index, false);
        visualizer.focusOnWaypoint(index);
      }
      
      // Scroll to waypoint in the left panel after a delay to allow React to update
      setTimeout(() => {
        const waypointElement = document.querySelector(`[data-waypoint-index="${index}"]`);
        if (waypointElement) {
          waypointElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, 150);
      
      // Jump robot to waypoint position using IK solver if enabled
      if (jumpToWaypointEnabled && currentWaypoints[index]) {
        const waypoint = currentWaypoints[index];

        if (!visualizer || !visualizer.robotLoaded || !visualizer.ur10) {
          showStatus('Robot not loaded. Cannot jump to waypoint position.', 'warning');
          return;
        }

        if (!ikSolver) {
          showStatus('IK solver not available for this robot type.', 'error');
          return;
        }

        try {
          // Compute IK for the waypoint pose
          showStatus('Computing IK solution...', 'info');

          // Convert waypoint pose from feature-local to world/robot-base coordinates
          let targetPose;

          if (waypoint.feature === 'Base') {
            // Waypoint is already in robot base frame - use directly
            targetPose = {
              position: { ...waypoint.position },
              rotation: { ...waypoint.rotation },
            };
          } else {
            // Find the feature and convert from feature-local to world coordinates
            const feature = currentFeatures.find(f => f.name === waypoint.feature);

            if (!feature) {
              showStatus(`✗ Feature "${waypoint.feature}" not found for waypoint ${waypoint.number}`, 'error');
              return;
            }

            // Convert from feature-local to world coordinates
            const worldPose = featureToWorld(waypoint.position, waypoint.rotation, feature);
            targetPose = {
              position: worldPose.position,
              rotation: worldPose.rotation,
            };
          }

          let solution = null;
          let usedSavedJoints = false;

          if (waypoint.jointPositions) {
            // Verify what pose the saved joints actually produce
            const savedJointsPose = ikSolver.computeFK(waypoint.jointPositions);

            // Calculate error between saved joints pose and target pose
            const posError = Math.sqrt(
              Math.pow(savedJointsPose.position.x - targetPose.position.x, 2) +
              Math.pow(savedJointsPose.position.y - targetPose.position.y, 2) +
              Math.pow(savedJointsPose.position.z - targetPose.position.z, 2)
            );

            // If saved joints are already accurate, use them directly
            if (posError < 0.001) { // < 1mm error
              solution = waypoint.jointPositions;
              usedSavedJoints = true;
            }
          }

          // If saved joints not available or not accurate, compute IK
          if (!solution) {
            const initialJoints = waypoint.jointPositions || null;
            solution = ikSolver.computeIK(targetPose, initialJoints);
          }

          if (solution) {
            // Convert radians to degrees for the robot
            const jointAngles = solution.map(rad => rad * 180 / Math.PI);
            visualizer.ur10.setJointAngles(jointAngles);

            // Verify the solution
            const verification = ikSolver.verifyIKSolution(solution, targetPose);
            const posErrorMm = (verification.posError * 1000).toFixed(2);
            const rotErrorDeg = (verification.rotError * 180 / Math.PI).toFixed(2);

            const solutionType = usedSavedJoints ? 'saved joints' : 'IK solver';
            showStatus(
              `✓ Robot moved to waypoint ${waypoint.number} using ${solutionType} (error: ${posErrorMm}mm, ${rotErrorDeg}°)`,
              'success'
            );
          } else {
            showStatus(
              `✗ IK Failed: No solution found for waypoint ${waypoint.number}. Position may be unreachable.`,
              'error'
            );
          }
        } catch (error) {
          console.error('Error computing IK:', error);
          showStatus(`✗ IK Error: ${error.message}`, 'error');
        }
      }
    };

    const handleViaPointSelected = (e) => {
      const waypointIndex = e.detail.waypointIndex;
      setSelectedWaypointIndex(waypointIndex);
      setSelectedIsViaPoint(true);
      clearMultiSelect();
      
      if (visualizer) {
        visualizer.selectWaypoint(waypointIndex, true);
        visualizer.focusOnWaypoint(waypointIndex);
      }
    };

    const handleFeatureSelected = (e) => {
      const index = e.detail.index;
      setSelectedFeatureIndex(index);
      // Clear waypoint selection when selecting a feature
      setSelectedWaypointIndex(null);
      setSelectedIsViaPoint(false);
      clearMultiSelect();
    };

    const handleWaypointDeselected = () => {
      setSelectedWaypointIndex(null);
      setSelectedIsViaPoint(false);
      setSelectedFeatureIndex(null);
      // Don't call visualizer.deselectWaypoint() here - it already triggered this event
    };

    document.addEventListener('waypointSelected', handleWaypointSelected);
    document.addEventListener('viaPointSelected', handleViaPointSelected);
    document.addEventListener('featureSelected', handleFeatureSelected);
    document.addEventListener('waypointDeselected', handleWaypointDeselected);

    return () => {
      document.removeEventListener('waypointSelected', handleWaypointSelected);
      document.removeEventListener('viaPointSelected', handleViaPointSelected);
      document.removeEventListener('featureSelected', handleFeatureSelected);
      document.removeEventListener('waypointDeselected', handleWaypointDeselected);
    };
  }, [visualizer, setSelectedWaypointIndex, setSelectedIsViaPoint, setSelectedFeatureIndex, clearMultiSelect, currentWaypoints, currentFeatures, jumpToWaypointEnabled, showStatus, ikSolver]);
}


